'use client';

/**
 * 页面级 SPM 作用域：让跨页面复用的共享组件（商品卡片、收藏、筛选等）
 * 拿到"当前页面终端"的编码表，从而在组件内部正确附加 spm_cnt。
 *
 * 用法：页面根或楼层处用 SpmScopeProvider 提供当前页编码表；
 * 共享组件用 useSpmPosition/useSpmScope 取编码——没有作用域时返回 undefined，
 * 调用点保持原样（对未接入 SPM 的页面完全向后兼容）。
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import type { SpmPageCodes } from './types';
import { resolveSpmPositionCode } from './position-code';

interface SpmScopeValue {
  page: string;
  codes: SpmPageCodes;
}

const SpmScopeContext = createContext<SpmScopeValue | null>(null);

export function SpmScopeProvider({
  page,
  codes,
  children,
}: {
  page: string;
  codes: SpmPageCodes;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ page, codes }), [page, codes]);
  return <SpmScopeContext.Provider value={value}>{children}</SpmScopeContext.Provider>;
}

export function useSpmScope(): SpmScopeValue | null {
  return useContext(SpmScopeContext);
}

/**
 * 取坑位完整编码（A.B.C.D）；未列出的数字坑位按同形已登记编码继续编号。
 * 无作用域或无对应编号模板时返回 undefined。
 * 传入的 module/position 需与编码表模块及编号模板一致；动态坑位由调用方传入实际序号（如 `product_001`）。
 */
export function useSpmPosition(): (module: string, position: string) => string | undefined {
  const scope = useContext(SpmScopeContext);
  return useCallback(
    (module: string, position: string) => {
      if (!scope) return undefined;
      return resolveSpmPositionCode(scope.codes[module], position);
    },
    [scope],
  );
}

/** 取模块曝光编码（A.B.C）；无作用域或编码表无该模块时返回 undefined。 */
export function useSpmModuleCode(): (module: string) => string | undefined {
  const scope = useContext(SpmScopeContext);
  return useCallback(
    (module: string) => {
      if (!scope) return undefined;
      const value = scope.codes[module]?.code;
      return typeof value === 'string' && value ? value : undefined;
    },
    [scope],
  );
}
